import {
  Anchor,
  Stack,
  Title,
  Text,
  Paper,
  Table,
  Select,
  Button,
  Switch,
  Checkbox,
  Divider,
  Group,
  SimpleGrid,
  NumberFormatter,
  Tooltip,
  useComputedColorScheme,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useEffect } from "react";
import { usePromptsStore } from "../Prompts/promptsStore";
import { Link } from "wouter";
import { useForm } from "@mantine/form";
import { useMonitorsStore } from "./monitorsStore";
import { useLlmProvidersStore } from "../LlmProviders/llmProvidersStore";
import LlmProviderLogo from "../LlmProviders/LlmProviderLogo";
import { groupProviders, providerGuide } from "../LlmProviders/providerGuide";
import { resolveSurface } from "../LlmProviders/googleSurfaces";
import { QUERY_TYPE_ORDER, queryTypeMeta } from "../_Shared/queryTypes";
import { useStore } from "../store";
import { useBrandsStore } from "./brandsStore";
import { notifications } from "@mantine/notifications";

/**
 * Active prompts grouped by industry, busiest bucket first.
 *
 * Industry is the grouping key every report row is built on, so this table is a preview of how many
 * rows the customer will get and how many prompts stand behind each. Sorting by weight puts the
 * buckets that can actually produce a rate at the top; an industry with no active prompt is not a
 * row at all, because it contributes nothing and used to fill the table with zeroes.
 */
function industryRows(prompts) {
  const active = (prompts ?? []).filter(
    (p) => p.status === "on" || p.status === "auto"
  );
  const byIndustry = new Map();
  for (const prompt of active) {
    const key = prompt.industry || "Ungrouped";
    if (!byIndustry.has(key))
      byIndustry.set(key, { industry: key, total: 0, counts: {} });
    const row = byIndustry.get(key);
    row.total += 1;
    row.counts[prompt.queryType] = (row.counts[prompt.queryType] ?? 0) + 1;
  }
  return [...byIndustry.values()].sort(
    (a, b) => b.total - a.total || a.industry.localeCompare(b.industry)
  );
}

/** A column header that explains what it is counting. */
function ExplainedTh({ queryType }) {
  const meta = queryTypeMeta(queryType);
  return (
    <Table.Th ta="right">
      <Tooltip label={meta.tooltip} withArrow multiline w={320}>
        <Group gap={4} justify="flex-end" wrap="nowrap" style={{ cursor: "help" }}>
          <Text fw={500} fz="sm">
            {meta.badgeLabel}
          </Text>
          <IconInfoCircle size={13} stroke={1.5} opacity={0.5} />
        </Group>
      </Tooltip>
    </Table.Th>
  );
}

function DataWarning({ expectedDataPoints }) {
  const tenantProfile = useStore((state) => state.tenantProfile);
  if (!tenantProfile?.usage) {
    return null;
  }
  const limit = tenantProfile.usage.dataPointsLimit;
  const current = tenantProfile.usage.dataPointsUsed;
  if (current + expectedDataPoints > limit) {
    return (
      <Text c="red" size="sm">
        Warning: This monitor will exceed your data points limit of{" "}
        {limit.toLocaleString()} data points per month. You can still create
        this monitor, but it will only run partially. Consider upgrading your
        plan or reducing the number of LLMs or prompts.
      </Text>
    );
  }
  return null;
}

function ownsTheShutter(plan) {
  return plan === "Enterprise" || plan === "SUPER_ADMIN";
}

export default function BrandMonitors({ params }) {
  const { prompts, fetchPrompts } = usePromptsStore();
  const { fetchBrands } = useBrandsStore();
  const { fetchMonitorsByBrandId, monitorsByBrand, updateMonitor } =
    useMonitorsStore();
  const { llmProviders } = useLlmProvidersStore();
  const tenantPlan = useStore((state) => state.tenantProfile?.plan);
  const canMixCadence = ownsTheShutter(tenantPlan);

  const form = useForm({
    // mode: "uncontrolled",
    initialValues: {
      frequencyInDays: "7",
      isActive: true,
      useSearch: true,
      llmProviderIds: [], // Assuming this is an array of selected LLM provider IDs
      detectionEnabled: true,
      detectionFrequencyInDays: "1",
      detectionProviderIds: [],
    },
  });

  useEffect(() => {
    fetchPrompts(params.brandId);
    fetchMonitorsByBrandId(params.brandId).then((monitors) => {
      // console.debug("Fetched monitors:", monitors);
      const formValues = {
        frequencyInDays: "" + (monitors[0]?.frequencyInDays ?? "7"),
        isActive: monitors[0]?.isActive ?? true,
        useSearch: monitors[0]?.useSearch ?? true,
        llmProviderIds: monitors[0]?.llmProviderIds || [],
        detectionEnabled: monitors[0]?.detectionEnabled ?? true,
        detectionFrequencyInDays:
          "" + (monitors[0]?.detectionFrequencyInDays ?? "1"),
        detectionProviderIds: monitors[0]?.detectionProviderIds || [],
      };
      // console.debug("Setting form values:", formValues);
      form.setValues(formValues);
      form.setInitialValues(formValues);
    });
  }, [params.brandId]);

  const monitor = monitorsByBrand
    ? monitorsByBrand[params.brandId]
      ? monitorsByBrand[params.brandId][0]
      : null
    : null;

  // Only the Google surfaces the monitor already tracks can be sampled daily — the API rejects
  // anything else, so offering it would be offering a box that cannot be saved.
  const detectableProviders = (llmProviders ?? []).filter(
    (p) => form.values.llmProviderIds.includes(p.id) && resolveSurface(p.name),
  );

  const expectedDataPoints = form.values.isActive
    ? (prompts?.filter((p) => p.status === "on" || p.status === "auto")
        .length || 0) *
      // 5 /* Assuming 5 samples */ *
      form.values.llmProviderIds.length *
      (30 / form.values.frequencyInDays).toFixed(0)
    : 0;

  // console.debug("Form values:", form.values);

  return (
    <>
      {/* <Title mt="md" order={2}>
        Brand Monitoring
      </Title> */}
      {/* 
      <BrandBreadcrumbs brandId={params.brandId} /> */}

      <Paper shadow="xs" p="md" mb="md">
        {/* Monitor Settings Form */}
        <form
          mt="md"
          component="form"
          onSubmit={form.onSubmit((values) => {
            const payload = canMixCadence
              ? values
              : {
                  frequencyInDays: monitor.frequencyInDays ?? 7,
                  isActive: values.isActive,
                  useSearch: monitor.useSearch ?? true,
                  llmProviderIds: monitor.llmProviderIds || [],
                  detectionEnabled: monitor.detectionEnabled ?? true,
                  detectionFrequencyInDays:
                    monitor.detectionFrequencyInDays ?? 1,
                  detectionProviderIds: monitor.detectionProviderIds || [],
                };
            updateMonitor(params.brandId, monitor.id, payload).then(() => {
              fetchBrands().then(() => {
                notifications.show({
                  title: "Monitor updated",
                  message: "Monitor settings have been updated successfully.",
                  color: "teal",
                });
              });
            });
          })}
        >
          {(() => {
            if (!monitor) {
              return <Text c="dimmed">No monitor settings available.</Text>;
            }

            const rows = industryRows(prompts);
            const activeCount = rows.reduce((sum, row) => sum + row.total, 0);
            // Industries that exist on some prompt but have nothing switched on. Counted rather
            // than listed, so switching a prompt back on is not a mystery.
            //
            // Clamped because rows can carry an "Ungrouped" bucket that the Set does not: a prompt
            // with no industry at all is a row without being a named industry.
            const dormantIndustries = Math.max(
              0,
              new Set((prompts ?? []).map((p) => p.industry).filter(Boolean))
                .size - rows.length
            );

            const providerCard = (item) => {
              const guide = providerGuide(item);
              return (
                <Checkbox.Card p="sm" radius="md" value={item.id} key={item.id}>
                  <Group wrap="nowrap" align="flex-start">
                    <Checkbox.Indicator />
                    <LlmProviderLogo llmProvider={item} size={32} />
                    <div style={{ minWidth: 0 }}>
                      <Group gap={4} wrap="nowrap">
                        <Text>{item.name}</Text>
                        <Tooltip label={guide.tooltip} withArrow multiline w={320}>
                          <IconInfoCircle
                            size={14}
                            stroke={1.5}
                            opacity={0.5}
                            style={{ cursor: "help", flexShrink: 0 }}
                          />
                        </Tooltip>
                      </Group>
                      <Text c="dimmed" fz="sm">
                        {guide.blurb}
                      </Text>
                    </div>
                  </Group>
                </Checkbox.Card>
              );
            };

            const includedProviders = (llmProviders ?? []).filter((p) =>
              form.values.llmProviderIds.includes(p.id)
            );

            return (
              <Stack spacing={0}>
                {canMixCadence ? (
                <Checkbox.Group
                  label="Pick what to monitor"
                  description="Each one is a separate place someone can be told about your brand"
                  value={form.values.llmProviderIds}
                  onChange={(values) =>
                    form.setFieldValue("llmProviderIds", values)
                  }
                >
                  {/* Grouped rather than one flat grid: the three Google entries are the ones
                      people confuse, and putting them side by side with an explanation is most of
                      the answer. cols=3 gives Google exactly one row. */}
                  {groupProviders(llmProviders).map((group) => (
                    <div key={group.key}>
                      <Group gap={6} mt="md" mb={4} wrap="nowrap">
                        <Text fz="sm" fw={600}>
                          {group.label}
                        </Text>
                        <Tooltip label={group.hint} withArrow multiline w={340}>
                          <IconInfoCircle
                            size={14}
                            stroke={1.5}
                            opacity={0.5}
                            style={{ cursor: "help" }}
                          />
                        </Tooltip>
                      </Group>
                      <SimpleGrid cols={3} gap="xs">
                        {group.providers.map(providerCard)}
                      </SimpleGrid>
                    </div>
                  ))}
                </Checkbox.Group>
                ) : (
                  <div>
                    <Text fz="sm" fw={600}>
                      Engines we include
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      All six on this plan. Cadence is weekly for the chat
                      models and daily for Google AI Mode / Overviews.
                    </Text>
                    <SimpleGrid cols={3} gap="xs">
                      {(includedProviders.length > 0
                        ? includedProviders
                        : llmProviders ?? []
                      ).map((item) => {
                        const guide = providerGuide(item);
                        return (
                          <Group
                            key={item.id}
                            wrap="nowrap"
                            align="flex-start"
                            gap="sm"
                          >
                            <LlmProviderLogo llmProvider={item} size={32} />
                            <div style={{ minWidth: 0 }}>
                              <Text>{item.name}</Text>
                              <Text c="dimmed" fz="sm">
                                {guide.blurb}
                              </Text>
                            </div>
                          </Group>
                        );
                      })}
                    </SimpleGrid>
                  </div>
                )}

                <Title order={4} mt="lg">
                  Prompt Overview
                </Title>

                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>
                        <Tooltip
                          label="One row here becomes one row in your reports. A row backed by a single prompt shows a score computed from a single sample, so fewer, fuller rows beat more, thinner ones."
                          withArrow
                          multiline
                          w={340}
                        >
                          <Group gap={4} wrap="nowrap" style={{ cursor: "help" }}>
                            <Text fw={500} fz="sm">
                              Industry
                            </Text>
                            <IconInfoCircle size={13} stroke={1.5} opacity={0.5} />
                          </Group>
                        </Tooltip>
                      </Table.Th>
                      {QUERY_TYPE_ORDER.map((queryType) => (
                        <ExplainedTh key={queryType} queryType={queryType} />
                      ))}
                      <Table.Th ta="right">Total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((row) => (
                      <Table.Tr key={row.industry}>
                        <Table.Td>{row.industry}</Table.Td>
                        {QUERY_TYPE_ORDER.map((queryType) => (
                          <Table.Td key={queryType} ta="right">
                            {/* A dash, not a 0. A column of zeroes reads as a failure; it only
                                means this bucket has no prompt of that type. */}
                            {row.counts[queryType] ? (
                              row.counts[queryType]
                            ) : (
                              <Text span c="dimmed" size="sm">
                                –
                              </Text>
                            )}
                          </Table.Td>
                        ))}
                        <Table.Td ta="right">
                          <Text fw={500} size="sm">
                            {row.total}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    {rows.length === 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={QUERY_TYPE_ORDER.length + 2}>
                          <Text c="dimmed" size="sm">
                            No prompts are switched on, so this monitor has
                            nothing to measure.
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    <Table.Tr>
                      <Table.Td colSpan={4}>
                        <Text size="sm">
                          Manage your prompts in the{" "}
                          {/* Anchor over a bare wouter Link: the raw <a> rendered as an
                              unstyled browser link against the dark theme. The href form is
                              deliberate — this page derives its active tab from ?tab=, so
                              navigating IS switching tabs, and it stays shareable. */}
                          <Anchor component={Link} to="/llm-config?tab=prompts">
                            Prompts tab
                          </Anchor>
                          {dormantIndustries > 0 &&
                            ` — ${dormantIndustries} further ${
                              dormantIndustries === 1 ? "industry has" : "industries have"
                            } no active prompt and are not shown.`}
                        </Text>
                      </Table.Td>
                      <Table.Td colSpan={QUERY_TYPE_ORDER.length - 2} ta="right">
                        <Text fw={500}>
                          Total prompts: {activeCount}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>

                <Group>
                  {canMixCadence && (
                    <>
                      <Select
                        label={null}
                        data={[
                          { value: "1", label: "Daily" },
                          { value: "7", label: "Weekly" },
                          { value: "14", label: "Bi-weekly" },
                          { value: "30", label: "Every 30 days" },
                        ]}
                        size="sm"
                        style={{ minWidth: 110 }}
                        {...form.getInputProps("frequencyInDays")}
                      />
                      <Switch
                        label="Web Search"
                        {...form.getInputProps("useSearch", { type: "checkbox" })}
                      />
                    </>
                  )}
                  <Switch
                    label="Active"
                    {...form.getInputProps("isActive", { type: "checkbox" })}
                  />
                  <Text size="sm" c="dimmed">
                    Last run:{" "}
                    {monitor.lastRun
                      ? new Date(monitor.lastRun).toLocaleString()
                      : "-"}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Next run:{" "}
                    {monitor.nextRun
                      ? new Date(monitor.nextRun).toLocaleString()
                      : "-"}
                  </Text>
                </Group>

                {canMixCadence ? (
                  <>
                {/* Daily detection has always run and has never been configurable: the two
                    fields behind it existed on the model with no way in, so a metered daily
                    charge could not be shaped or declined. It sits under the weekly settings
                    because it reads the surfaces chosen above, not a set of its own. */}
                <Divider
                  my="xs"
                  label={<Text size="xs">Daily detection</Text>}
                  labelPosition="left"
                />

                <Text size="sm" c="dimmed">
                  A cheap daily check of whether your brand appears at all — a fetch and a
                  string match, no model call. It is what the Detection page reports, and what
                  gives a rate enough samples to mean anything. Google surfaces only: reading an
                  LLM daily costs about ten times as much, because there the fetch is the model
                  call.
                </Text>

                <Group>
                  <Switch
                    label="Sample daily"
                    {...form.getInputProps("detectionEnabled", { type: "checkbox" })}
                  />
                  <Select
                    label={null}
                    data={[
                      { value: "1", label: "Every day" },
                      { value: "2", label: "Every 2 days" },
                      { value: "7", label: "Weekly" },
                    ]}
                    size="sm"
                    style={{ minWidth: 140 }}
                    disabled={!form.values.detectionEnabled}
                    {...form.getInputProps("detectionFrequencyInDays")}
                  />
                  <Text size="sm" c="dimmed">
                    Next check:{" "}
                    {monitor.nextDetectionRun
                      ? new Date(monitor.nextDetectionRun).toLocaleString()
                      : "-"}
                  </Text>
                </Group>

                {detectableProviders.length > 0 && (
                  <Checkbox.Group
                    label="Sample these surfaces"
                    description="Leave all ticked to follow the monitor. Each surface costs about $0.004 per prompt per check."
                    value={
                      // Empty means "every surface this monitor tracks", so the boxes show what
                      // that resolves to rather than an empty group that contradicts the label.
                      form.values.detectionProviderIds.length > 0
                        ? form.values.detectionProviderIds
                        : detectableProviders.map((p) => p.id)
                    }
                    onChange={(values) =>
                      form.setFieldValue("detectionProviderIds", values)
                    }
                  >
                    <Group mt="xs">
                      {detectableProviders.map((provider) => (
                        <Checkbox
                          key={provider.id}
                          value={provider.id}
                          label={provider.name}
                          disabled={!form.values.detectionEnabled}
                        />
                      ))}
                    </Group>
                  </Checkbox.Group>
                )}

                <DataWarning expectedDataPoints={expectedDataPoints} />
                <Group wrap="nowrap" justify="space-between" mt="md">
                  <Text fw={500}>
                    This monitor will create{" "}
                    <NumberFormatter
                      thousandSeparator
                      value={expectedDataPoints}
                    />{" "}
                    data points per month
                  </Text>
                  <Button type="submit" disabled={!form.isDirty()}>
                    Save
                  </Button>
                </Group>
                  </>
                ) : (
                  <>
                    <Text size="sm" c="dimmed" mt="sm">
                      LLM analysis runs weekly. Google AI Mode and AI Overviews
                      are checked daily. Next check:{" "}
                      {monitor.nextDetectionRun
                        ? new Date(monitor.nextDetectionRun).toLocaleString()
                        : "—"}
                      .
                    </Text>
                    <Group justify="flex-end" mt="md">
                      <Button type="submit" disabled={!form.isDirty()}>
                        Save
                      </Button>
                    </Group>
                  </>
                )}
              </Stack>
            );
          })()}
        </form>
      </Paper>
    </>
  );
}
